/**
 * Loading the character spritesheets produced by tools/sims-pipeline.
 *
 * The sheet format is documented in
 * `tools/sims-pipeline/contracts/ATLAS_MANIFEST.md`. The two things this module
 * has to get right are the layer order and the pivot:
 *
 * - Layers composite back to front as `body → bottom → top → hair`, and every
 *   layer of a pose is a full-frame render of the same pose, so the four sprites
 *   share one transform and need no per-layer offset.
 * - The pivot is the **feet anchor** (world origin at render time), not the mesh
 *   centre. Frames are trimmed, so the pivot inside a trimmed frame is
 *   `simsAnchor - spriteSourceSize`, which is what `anchorFor()` returns.
 */
import * as PIXI from 'pixi.js';
import { smoothWhenMinified } from '../render/textureFilter';

export const CHARACTER_LAYERS = ['body', 'bottom', 'top', 'hair'] as const;
export type CharacterLayer = (typeof CHARACTER_LAYERS)[number];

export type ClipName = 'Idle_Loop' | 'Walk_Loop';

export interface SheetEntry {
  item: string;
  layer: CharacterLayer;
  json: string;
  png: string;
  frames: number;
  clips: Record<string, Record<string, number>>;
}

export interface AtlasIndex {
  sheets: SheetEntry[];
  anchor: { x: number; y: number };
  supersample: number;
  /**
   * Real-time playback rate of each clip: sheet frames ÷ the source clip's
   * duration in seconds. The packer measures it from the animation itself, so
   * a clip that was sampled sparsely (idle: 16 frames over ~10 s) plays as
   * slowly on screen as it does in the vendor's viewer.
   */
  clip_fps: Record<string, number>;
}

/** One recipe: which item supplies each layer. `null` = layer not drawn. */
export interface CharacterRecipe {
  body: string;
  top: string | null;
  bottom: string | null;
  hair: string | null;
}

export interface LoadedSheet {
  spritesheet: PIXI.Spritesheet;
  anchor: { x: number; y: number };
  /** frame name lists, `clips[clip][dir]` in play order */
  clips: Record<string, Record<string, string[]>>;
}

export const CHARACTER_ASSET_BASE = '/game-assets/character';

let indexPromise: Promise<AtlasIndex> | null = null;

export function loadAtlasIndex(): Promise<AtlasIndex> {
  if (!indexPromise) {
    // PIXI.Assets, not fetch: these are static game assets, and the repo's lint
    // rule reserves fetch for the generated API client.
    indexPromise = PIXI.Assets.load<AtlasIndex>({
      src: `${CHARACTER_ASSET_BASE}/index.json`,
      parser: 'loadJson',
    });
  }
  return indexPromise;
}

const sheetCache = new Map<string, Promise<LoadedSheet>>();

export function loadSheet(item: string): Promise<LoadedSheet> {
  const cached = sheetCache.get(item);
  if (cached) return cached;

  const promise = (async () => {
    // Assets.load on a spritesheet JSON resolves its image relative to the JSON
    // and parses it in one step.
    const spritesheet = await PIXI.Assets.load<PIXI.Spritesheet>(
      `${CHARACTER_ASSET_BASE}/${item}.json`,
    );
    // She is drawn at ~0.45 of the sheet's own scale at the default zoom, so
    // the sheet needs the same minification filtering as the furniture.
    smoothWhenMinified(spritesheet.textureSource);

    const meta = spritesheet.data.meta as unknown as {
      simsAnchor: { x: number; y: number };
      simsClips: Record<string, Record<string, string[]>>;
    };
    return { spritesheet, anchor: meta.simsAnchor, clips: meta.simsClips };
  })();

  sheetCache.set(item, promise);
  return promise;
}

export interface RecipeSheets {
  layers: Partial<Record<CharacterLayer, LoadedSheet>>;
  anchor: { x: number; y: number };
  supersample: number;
  /** see {@link AtlasIndex.clip_fps} — measured, never guessed by the renderer */
  clipFps: Record<string, number>;
}

export async function loadRecipe(recipe: CharacterRecipe): Promise<RecipeSheets> {
  const index = await loadAtlasIndex();
  const byItem = new Map(index.sheets.map((s) => [s.item, s]));

  const wanted: Array<[CharacterLayer, string | null]> = [
    ['body', recipe.body],
    ['bottom', recipe.bottom],
    ['top', recipe.top],
    ['hair', recipe.hair],
  ];

  const layers: Partial<Record<CharacterLayer, LoadedSheet>> = {};
  await Promise.all(
    wanted.map(async ([layer, item]) => {
      if (!item) return;
      if (!byItem.has(item)) {
        throw new Error(`recipe references unknown character item "${item}"`);
      }
      layers[layer] = await loadSheet(item);
    }),
  );

  return {
    layers,
    anchor: index.anchor,
    supersample: index.supersample,
    clipFps: index.clip_fps ?? {},
  };
}

/**
 * Anchor for a frame, in 0..1 sprite space.
 *
 * A PixiJS anchor is a fraction of `texture.orig` — the *untrimmed* size — and
 * PixiJS already offsets the trimmed pixels inside that box for you. So the
 * anchor is just `simsAnchor / orig`, with no trim term.
 *
 * Subtracting the trim here (the obvious-looking thing to do) shifts every layer
 * by its own trim offset, and since the body, top, bottom and hair all trim to
 * different boxes, they come apart on screen — trousers in one place, torso in
 * another. Registration between layers depends on this staying trim-free.
 */
export function anchorFor(
  texture: PIXI.Texture,
  atlasAnchor: { x: number; y: number },
): { x: number; y: number } {
  const w = texture.orig.width;
  const h = texture.orig.height;
  if (!w || !h) return { x: 0.5, y: 1 };
  return { x: atlasAnchor.x / w, y: atlasAnchor.y / h };
}
